// src/components/Layout/Menu/Menu.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  createSearchParams,
  NavLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { AnyObject } from '@triare/auth-redux';
import { LogoutOutlined } from '@ant-design/icons';
import { Menu as AntdMenu, Button } from 'antd';
import { connect } from 'react-redux';
import { signOut } from '@triare/auth-redux/dist/saga/auth/signOut';
import { useMediaQuery } from 'react-responsive';
import { SignUpAction } from '@triare/auth-redux/dist/saga/auth/signUp';

import store, { RootState } from '../../../store';
import { moduleName, useAuth, User } from '../../../store/auth';
import { useSimpleModal } from '../../Common/Modal/Simple';
import { doesUserHaveAccess } from '../../../routes/PrivateRoute';

import EmailAndPassword from '../../Pages/Auth/SignIn/EmailAndPassword';
import SignIn from '../../Pages/Auth/SignIn';
import SignUp from '../../Pages/Auth/SignUp';
import SignUpContent from '../../Pages/Auth/SignUp/EmailAndPassword';
import ForgotPassword from '../../Pages/Auth/ForgotPassword';
import Information from '../../Pages/Auth/SignUp/Information';

import { routes } from './menuRoutes';
import type { MenuRoute } from './menuRoutes';

import { IconCommunity } from '../../Common/Icon';
import styles from './index.module.scss';

export const paramsToString = (
  user?: User | null,
  params?: ((user: User) => string | AnyObject) | AnyObject | string
) => {
  if (!user || !params) return '';
  if (typeof params === 'string') return `?${params}`;
  if (typeof params === 'function') {
    let result = params(user);
    if (!result) return '';
    if (typeof result === 'object')
      result = createSearchParams(result).toString();
    return result ? `?${result}` : '';
  }
  const q = createSearchParams(params).toString();
  return q ? `?${q}` : '';
};

import { Route as AppRoute } from '../../../routes';
import { a } from 'framer-motion/dist/types.d-B50aGbjN';

const createItem = (
  { route, children, ignoreMenu, privateRoute, ...data }: AppRoute,
  user: User
) => {
  let childrenList: MenuRoute[] | undefined;

  if (children?.length) {
    childrenList = children
      .filter(({ ignoreMenu: chIgnore }) => chIgnore !== true)
      .map(({ ignoreMenu: _i, privateRoute: _p, ...child }) => ({
        ...child,
        label: child.route ? (
          <NavLink
            to={`/${child.route.path}${paramsToString(
              user,
              child.route.params
            )}`}
          >
            {child.name || child.key}
          </NavLink>
        ) : (
          child.name
        ),
        key: child.route
          ? `/${child.route.path}`
          : `/${child.key || child.name}`,
      }));
  }

  const nameText =
    typeof data.name === 'function' ? data.name(user) : data.name;
  const to = `/${route?.path ?? ''}${paramsToString(user, route?.params)}`;

  const { icon, className, ...restData } = (data as any) ?? {};
  const hasChildren = !!childrenList?.length;

  const rightIcon = icon
    ? React.cloneElement(icon as React.ReactElement, {
        className: `${styles.allAboutIcon} allAboutIcon`,
      })
    : null;

  const label = hasChildren ? (
    <span className={styles.submenuTitle}>
      <span>{nameText}</span>
      {rightIcon}
    </span>
  ) : route ? (
    <NavLink to={to}>{nameText}</NavLink>
  ) : (
    nameText
  );

  return {
    ...restData,
    className: [className, hasChildren && rightIcon ? 'with-right-chevron' : '']
      .filter(Boolean)
      .join(' '),
    children: hasChildren ? childrenList : undefined,
    label,
    key: route ? `/${route.path}` : `/${restData.key || nameText}`,
  };
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function filter(list: any[], user: User) {
  const cloned = list.map((item) => ({
    ...item,
    children: item.children ? [...item.children] : undefined,
  }));

  return cloned.filter((item) => {
    if (item?.children?.length) {
      // eslint-disable-next-line no-param-reassign
      item.children = filter(item.children, user);
    }
    return item?.roles ? doesUserHaveAccess(item.roles, user) : true;
  });
}

const menuLeft = routes
  .filter(({ ignoreMenu }) => ignoreMenu !== true)
  .filter(({ align }) => align === 'left')
  .map((item) => ({
    ...item,
    path: item.route?.path?.split('/:')[0],
    children: item.children?.filter(({ ignoreMenu }) => ignoreMenu !== true),
  }));

const menuRight = routes
  .filter(({ ignoreMenu }) => ignoreMenu !== true)
  .filter(({ align }) => align === 'right');

interface MenuProps {
  user: User;
  mode?: 'vertical' | 'horizontal';
}

function Menu({ user }: MenuProps): React.ReactElement {
  const navigate = useNavigate();
  const { authorized } = useAuth();
  const { open, contextHolder } = useSimpleModal();
  const { pathname } = useLocation();

  const [isSignInOpen, setSignInOpen] = useState(false);
  const [isSignUpOpen, setSignUpOpen] = useState(false);
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [informationPayload, setInformationPayload] = useState<
    Partial<SignUpAction>
  >({});
  const [menuKey, setMenuKey] = useState(0);

  useEffect(() => {
    const handleResize = () => setMenuKey((k) => k + 1);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isForgotOpen) setSignInOpen(false);
  }, [isForgotOpen]);

  const menuLeftFiltered = useMemo(
    () =>
      filter(
        menuLeft.map((data) =>
          createItem(
            {
              ...data,
              route: { ...data.route, path: data.route?.path?.split('/:')[0] },
            } as AppRoute,
            user
          )
        ),
        user
      ).map((item) => ({
        ...item,
        name: typeof item.name === 'function' ? item.name(user) : item.name,
        icon: typeof item.icon === 'function' ? item.icon(user) : item.icon,
        children: item.children?.length ? item.children : undefined,
      })),
    [user]
  );

  const menuRightFiltered = useMemo(() => {
    const list = filter(
      menuRight.map((data) => createItem(data as AppRoute, user)),
      user
    ).map((item) => ({
      ...item,
      name: typeof item.name === 'function' ? item.name(user) : item.name,
      icon: typeof item.icon === 'function' ? item.icon(user) : item.icon,
      popupClassName: styles.rightMenuPopup,
      popupOffset: [-50, -10],
    }));

    for (const parent of list) {
      if (parent.children?.length) {
        for (const child of parent.children) {
          if (child?.key === '/Sign out') {
            child.onClick = () => {
              open({
                icon: <LogoutOutlined style={{ color: '#FF4D4F' }} />,
                title: 'Log out',
                content: 'Are you sure you want to Log out?',
                cancelText: 'Cancel',
                centered: true,
                okText: 'Yes',
                onOk: () => {
                  store.dispatch(signOut());
                  navigate('/');
                },
              });
            };
          }
        }
      }
    }

    return list;
  }, [user, navigate, open]);

  const activeMenuItem = `/${pathname.split('/')[1]}`;
  const isTablet = useMediaQuery({ query: '(max-width: 1024px)' });

  return (
    <div className={styles.menuWrapper}>
      <div className={styles.menuCenter}>
        <AntdMenu
          theme="dark"
          key={menuKey}
          mode={isTablet ? 'inline' : 'horizontal'}
          defaultSelectedKeys={[activeMenuItem]}
          selectedKeys={[activeMenuItem]}
          items={menuLeftFiltered as any}
          style={{ background: 'none' }}
          triggerSubMenuAction={isTablet ? 'click' : 'hover'}
        />
      </div>

      <div className={styles.rightItems}>
        {authorized ? (
          <AntdMenu
            className={styles.rightMenu}
            theme="dark"
            mode="horizontal"
            key={menuKey + 1}
            selectable={false}
            defaultSelectedKeys={[activeMenuItem]}
            selectedKeys={[activeMenuItem]}
            items={menuRightFiltered as any}
            overflowedIndicator={null}
            style={{
              minWidth: '248px',
              minHeight: '48px',
              fontWeight: 700,
              fontSize: '22px!important',
              justifyContent: isTablet ? 'center' : 'end',
              background: 'none',
            }}
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              type="text"
              style={{ color: '#FFFFFF' }}
              className={styles.logInBtn}
              onClick={(e) => {
                e.stopPropagation();
                setSignInOpen(true);
              }}
            >
              <img
                src="/media/user-logo.svg"
                alt="Login Icon"
                className={styles.icon}
              />
              Login
            </Button>

            <SignIn
              content={EmailAndPassword}
              isOpen={isSignInOpen}
              onClose={() => setSignInOpen(false)}
              isSignUpOpen={isSignUpOpen}
              setSignUpOpen={setSignUpOpen}
              setForgotOpen={() => setIsForgotOpen(true)}
            />
            <SignUp
              content={SignUpContent}
              isOpen={isSignUpOpen}
              onClose={() => setSignUpOpen(false)}
              setSignInOpen={() => setSignInOpen(true)}
              signUpSuccess={(payload) => setInformationPayload(payload)}
            />
            <ForgotPassword
              isOpen={isForgotOpen}
              onClose={() => setIsForgotOpen(false)}
              setSignInOpen={() => setSignInOpen(true)}
            />
            <Information
              prevPayload={informationPayload}
              isOpen={!!informationPayload?.email}
              onClose={() => setInformationPayload({})}
              setSignUpOpen={() => setSignUpOpen(true)}
            />
          </div>
        )}

        {!authorized ? (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Button
              size="large"
              type="primary"
              onClick={(e) => {
                e.stopPropagation();
                setSignInOpen(true);
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconCommunity /> Join the Community
            </Button>
          </div>
        ) : null}

        {contextHolder}
      </div>
    </div>
  );
}

export default connect((state: RootState) => ({
  user: state[moduleName].user,
}))(Menu);
